import { mm } from "@inovitas/infra3dsdk";
import {
  Checkbox,
  FormControl,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableCell,
  Typography,
} from "@mui/material";
import { Feature } from "geojson";
import React from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { manageKeyboardEvents } from "shared/common/utils/keyboardEvents/manageKeyboardEvents";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import { returnType } from "./cellFunctions/returnType";
import { returnValue } from "./cellFunctions/returnValue";
import { FeatureColumn } from "./FeatureListTable";

interface props {
  item: Feature;
  col: FeatureColumn;
  indexCol: number;
  onChange: (value: string, item: Feature, columnName: string) => void;
}

const FeatureCell = ({ item, col, indexCol, onChange }: props) => {
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const editFeatureTableRow = sidebarStore(
    (state) => state.editFeatureTableRow
  );
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );
  const enableEditFeature = sidebarStore((state) => state.enableEditFeature);

  const sameRow = item?.id === editFeatureTableRow?.id;

  //activate and deactivate keyboard events while writing on input fields
  const handleKeyboardEvents = (state: boolean) => {
    if (viewer) {
      manageKeyboardEvents(viewer, state);
      if (!state) {
        const layer = viewer.getScene().getLayerHandler().getActiveLayer();
        if (layer === null) return;
        changeFeatureTableAddedRows(getChangedFeaturesList(layer));
      }
    }
  };

  return (
    <TableCell
      sx={{
        padding: "4px",
      }}
      key={indexCol}
    >
      {enableEditFeature && sameRow && col.editable ? (
        col.datatype === mm.LAYERPROPERTYTYPE.ENUM ? (
          <FormControl
            classes={{
              root: darkTheme
                ? "outlindedFeatureSelectInputWhite"
                : "outlindedFeatureSelectInput",
            }}
            size="small"
            sx={{ m: 0 }}
          >
            <Select
              aria-labelledby="demo-radio-buttons-group-label"
              value={item.properties ? item.properties[col.key] : ""}
              name="enum-select"
              onChange={(event: SelectChangeEvent) => {
                onChange(event.target.value, item, col.key);
                handleKeyboardEvents(false);
              }}
              classes={{
                select: darkTheme
                  ? "editFeatureSelectInputWhite"
                  : "editFeatureSelectInput",
              }}
              sx={{ fontSize: "0.8rem", minHeight: "auto" }}
            >
              <MenuItem
                value=""
                classes={{
                  root: darkTheme
                    ? "editFeatureSelectInputWhite"
                    : "editFeatureSelectInput",
                }}
                id="editFeatureSelectInputNone"
                sx={{ fontSize: "0.8rem", minHeight: "auto" }}
              >
                -
              </MenuItem>
              {col.values &&
                col.values.map((val: string | number, index: number) => {
                  return (
                    <MenuItem
                      classes={{
                        root: darkTheme
                          ? "editFeatureSelectInputWhite"
                          : "editFeatureSelectInput",
                      }}
                      sx={{ fontSize: "0.8rem", minHeight: "auto" }}
                      key={index + "_" + val}
                      value={val}
                    >
                      {val}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        ) : col.datatype === mm.LAYERPROPERTYTYPE.BOOLEAN ? (
          <Checkbox
            size="small"
            classes={{
              root: darkTheme
                ? "editFeatureRadioButtonAccentWhite"
                : "editFeatureRadioButtonAccent",
              checked: darkTheme
                ? "editFeatureRadioButtonAccentWhite"
                : "editFeatureRadioButtonAccent",
            }}
            sx={{ padding: "0px" }}
            defaultChecked={
              item.properties ? Boolean(item.properties[col.key]) : false
            }
            onChange={(event, checked: boolean) => {
              onChange(String(checked), item, col.key);
              handleKeyboardEvents(false);
            }}
          />
        ) : col.datatype === mm.LAYERPROPERTYTYPE.DATE ? (
          <Input
            disableUnderline
            sx={{
              border: "none",
              fontSize: "12px",
              padding: "2px 4px",
              color: darkTheme ? "white" : "darkblue.main",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
            }}
            type={
              item.properties
                ? returnType(item.properties[col.key], col.datatype)
                : "number"
            }
            defaultValue={
              item.properties ? item.properties[col.key]?.split("+")[0] : ""
            }
            onFocus={() => handleKeyboardEvents(true)}
            onBlur={(event) => {
              onChange(event.target.value, item, col.key);
              handleKeyboardEvents(false);
            }}
          />
        ) : (
          <Input
            disableUnderline
            sx={{
              border: "none",
              fontSize: "12px",
              padding: "2px 4px",
              color: darkTheme ? "white" : "darkblue.main",
              backgroundColor: darkTheme ? "primary.light" : "grey.light",
            }}
            type={
              item.properties
                ? returnType(item.properties[col.key], col.datatype)
                : "number"
            }
            defaultValue={item.properties ? item.properties[col.key] : ""}
            onChange={(event) => {
              if (!item.properties) return;
              onChange(event.target.value, item, col.key);
            }}
            onFocus={() => handleKeyboardEvents(true)}
            onBlur={() => handleKeyboardEvents(false)}
          />
        )
      ) : (
        <Typography
          sx={{
            fontSize: "12px",
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          {col.key != "id" && item.properties
            ? returnValue(item.properties[col.key])
            : returnValue(item.id)}
        </Typography>
      )}
    </TableCell>
  );
};
export default FeatureCell;
